import { createPortal } from "react-dom";
import {useCallback, useEffect, useLayoutEffect, useRef, useState,} from "react";
import {FaEllipsisVertical} from "react-icons/fa6";
import Button from "./button.jsx";

function generateDropdownId() {
    if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
    ) {
        return crypto.randomUUID();
    }
    return `dropdown-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function DropdownButton({buttonContent, buttonClassName = "", menuClassName = "", itemClassName = "", items = [], align = "right", blurOnClose = true, menuWidth = 160, offset = 8, renderButton }) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);

    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const dropdownIdRef = useRef(generateDropdownId());

    const stopEvent = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const closeMenu = useCallback(() => {
        setOpen(false);
        setPosition(null);

        if (blurOnClose) {
            buttonRef.current?.blur();
        }
    }, [blurOnClose]);

    const updateMenuPosition = useCallback(() => {
        const button = buttonRef.current;

        if (!button) return;

        const rect = button.getBoundingClientRect();

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const menuHeight = menuRef.current?.offsetHeight ?? 0;

        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        const shouldOpenUp = menuHeight > 0 && spaceBelow < menuHeight + offset && spaceAbove > spaceBelow;

        const rawLeft = align === "left" ? rect.left : rect.right - menuWidth;

        const safeLeft = Math.min(
            Math.max(rawLeft, 8),
            viewportWidth - menuWidth - 8
        );

        const top = shouldOpenUp ? rect.top - menuHeight - offset : rect.bottom + offset;

        setPosition({
            top: Math.max(top, 8),
            left: safeLeft,
            width: menuWidth,
        });
    }, [align, menuWidth, offset]);

    const toggleMenu = useCallback((event) => {
        stopEvent(event);

        if (items.length === 0) return;

        setOpen((current) => {
            const nextOpen = !current;

            if (nextOpen) {
                window.dispatchEvent(new CustomEvent("dropdown-button-open", {
                    detail: {
                        id: dropdownIdRef.current,
                    },
                }));

                updateMenuPosition();
            }

            return nextOpen;
        });
    }, [items.length, stopEvent, updateMenuPosition]);

    const handleItemClick = useCallback((event, item) => {
        event.stopPropagation();

        if (item.disabled) return;

        const result = item.onClick?.(item);

        if (result && typeof result.then === "function") {
            return result.finally(closeMenu);
        }

        closeMenu();
        return result;
    }, [closeMenu]);

    useLayoutEffect(() => {
        if (!open) return;

        updateMenuPosition();

        function handleClickOutside(event) {
            const clickedButton = buttonRef.current?.contains(event.target);
            const clickedMenu = menuRef.current?.contains(event.target);
            const clickedSweetAlert = event.target?.closest?.(".swal2-container");

            if (!clickedButton && !clickedMenu && !clickedSweetAlert) {
                closeMenu();
            }
        }

        function handleKeyDown(event) {
            if (event.key === "Escape") {
                closeMenu();
            }
        }

        function handleWindowChange() {
            updateMenuPosition();
        }

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", handleWindowChange);
        window.addEventListener("scroll", handleWindowChange, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", handleWindowChange);
            window.removeEventListener("scroll", handleWindowChange, true);
        }
    }, [open, closeMenu, updateMenuPosition]);

    useEffect(() => {
        function handleAnotherDropdownOpen(event) {
            const openedDropdownId = event.detail?.id;

            if (openedDropdownId !== dropdownIdRef.current) {
                closeMenu();
            }
        }

        window.addEventListener("dropdown-button-open", handleAnotherDropdownOpen);

        return () => {
            window.removeEventListener("dropdown-button-open", handleAnotherDropdownOpen);
        }
    }, [closeMenu]);

    const buttonProps = {
        ref: buttonRef,
        type: "button",
        className: buttonClassName,
        onClick: toggleMenu,
        onMouseDown: (event) => event.stopPropagation(),
        onDoubleClick: (event) => event.stopPropagation(),
        "aria-haspopup": "menu",
        "aria-expanded": open,
    };

    return (
        <>
            {renderButton ? (
                renderButton(buttonProps)
            ) : (
                <button {...buttonProps}>
                    {buttonContent ?? <FaEllipsisVertical />}
                </button>
            )}

            {open && position && createPortal(
                <div
                    ref={menuRef}
                    role="menu"
                    className={`
                        fixed z-[9999] overflow-hidden rounded-xl
                        border border-[#E5E7EB] bg-white shadow-lg outline-none
                        ${menuClassName}
                    `}
                    style={{
                        top: position.top,
                        left: position.left,
                        minWidth: position.width,
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => event.stopPropagation()}
                >
                    {items.map((item, index) => {
                        const Icon = item.icon;
                        const disabled = item.disabled;
                        const itemKey = item.key ?? item.label ?? index;
                        const resolvedItemClassName = `
                                    flex w-full items-center gap-2 px-3 py-2 text-sm transition
                                    first:rounded-t-xl last:rounded-b-xl
                                    ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-[#F9FAFB]"}
                                    ${item.danger ? "text-red-800" : "text-[#6a7282]"}
                                    ${itemClassName}
                                    ${item.className || ""}
                                `;
                        const content = (
                            <>
                                {Icon && <Icon size={item.iconSize} className="shrink-0 text-sm" />}
                                <span className="truncate">
                                    {item.label}
                                </span>
                            </>
                        );

                        if (item.actionFeedbackType) {
                            return (
                                <Button
                                    key={itemKey}
                                    unstyled
                                    type="button"
                                    role="menuitem"
                                    disabled={disabled}
                                    onClick={(event) => handleItemClick(event, item)}
                                    className={resolvedItemClassName}
                                    actionFeedbackType={item.actionFeedbackType}
                                    deferActionFeedback={item.deferActionFeedback}
                                    loadingText={item.loadingText}
                                    successText={item.successText}
                                    successClassName=""
                                >
                                    {content}
                                </Button>
                            );
                        }

                        return (
                            <button
                                key={itemKey}
                                type="button"
                                role="menuitem"
                                disabled={disabled}
                                onClick={(event) => handleItemClick(event, item)}
                                className={resolvedItemClassName}
                            >
                                {content}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </>
    )
}